/* eslint-disable @typescript-eslint/no-explicit-any */
import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import loadAds from '@jobseeker/common-core/helpers/loadAds';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import jobFeedService from '@jobseeker/common-core/services/jobfeed.service';
import {AppState, wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {Chat} from '@components';
import {metaInfo} from '@configs';
import {
  CAT_LOCATION_BASED,
  HIRIST_CATEGORY_ROUTE_MAPPING,
  JOBFEED,
  LOCATION,
  LOCATION_BASED,
  TAG_ID_HIRIST,
} from '@constants';
import {loadSEO, getMultiApplyVariant} from '@helpers';
import {JobFeed, NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {
  capitalizeFirstLetter,
  capitalizeWords,
  getConfigByApp,
  getIdBySlug,
  getMatchingCityName,
  getSelectedLocationIds,
  isUserAgentForBot,
  removedUndefinedFromArrayObject,
  slugToTitle,
  utilServerSideDeviceDetection,
} from '@utils';
import {getListSEOContent, getPageUrl} from '@utils/seo';

import type {GetServerSidePropsContext} from 'next/types';
import {useState, useEffect, useRef} from 'react';
import {
  getExpFilter,
  getUbaPageName,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {
  jobDetailService,
  jobfeedService,
} from '@jobseeker/common-core/services';
import cookie from 'cookie';
import {gladiatorService} from '@jobseeker/common-core/services';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import {hiristLocations} from '@jobseeker/lotus/components/JobSearchBar/data';

type FeedPageProps = {
  userIdSSR: number;
  slug: string;
  title: string;
  id: string;
  metaData: object;
  seoConfig: object;
  calledType: string;
  filters: {
    loc: any;
    page: number;
  };
  nearByCitiesJobs: any;
  showMultiApply: boolean;
};

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
const normalizeLocation = (value?: string) =>
  value
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim() ?? '';

const HIRIST_LOCATION_LABELS = new Set(
  hiristLocations.map(city => normalizeLocation(city.label))
);

const JobWithLocationPage = ({
  userIdSSR,
  title,
  id,
  metaData,
  calledType,
  filters,
  seoConfig,
  nearByCitiesJobs,
  showMultiApply,
}: FeedPageProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);

  dispatch({
    type: 'AppContext/set_recap',
    payload: title,
  });

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: title,
      item: '',
    },
  ];
  const router = useRouter();
  const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);
  const count1Year = useSelector((state: AppState) => state.job.count1Year);
  const noIndexNoFollow = count1Year === 0;
  const hasTracked = useRef(false);
  const initialPageTracked = useRef(false);
  const hasPageNoChanged = useRef(false);
  const hasFilterChanged = useRef(false);
  const [pageNumber, setPageNumber] = useState(filters?.page + 1);

  let sessionFilter: {minexp?: string; maxexp?: string} = {};
  try {
    sessionFilter = getExpFilter(LOCATION_BASED);
  } catch (error) {}
  const [currentFilters, setCurrentFilters] = useState<{
    minexp?: string;
    maxexp?: string;
    industry?: string[];
  }>({
    minexp: (router.query.minexp as string) || (sessionFilter.minexp as string),
    maxexp: (router.query.maxexp as string) || (sessionFilter.maxexp as string),
    industry: (router.query.industry as string)?.split(',') || [],
  });

  // Reset tracking when page number changes
  useEffect(() => {
    hasPageNoChanged.current = true;
  }, [pageNumber]);

  // Reset tracking when filter changes
  useEffect(() => {
    if (initialPageTracked.current) {
      hasFilterChanged.current = true;
    }
  }, [currentFilters]);

  useEffect(() => {
    const sessionUserId = userIdSSR || userId || '-1';

    if (hasTracked.current) return;

    const filterLocation = Array.isArray(filters?.loc)
      ? filters?.loc.join(',')
      : filters?.loc;

    if (id === 'product-jobs') {
      TrackEventUBA('productJobsPageView', 'view', {
        ref: router.query.ref || '',
        filterExp: router.query.minexp
          ? `${router.query.minexp}-${router.query.maxexp}`
          : '',
        filterLocation,
        page: getUbaPageName(),
        pageName: 'ProductCompanyListingPage',
        sessionUsername: sessionUserId,
      });
    }

    if (id === 'ecommerce-jobs') {
      TrackEventUBA('ecommerceJobsPageView', 'view', {
        ref: router.query.ref || '',
        page: getUbaPageName(),
        sessionUsername: sessionUserId,
      });
    }

    if (id === 'fintech-edtech-jobs') {
      TrackEventUBA('fintechJobsPageView', 'view', {
        ref: router.query.ref || '',
        page: getUbaPageName(),
        sessionUsername: sessionUserId,
      });
    }
    hasTracked.current = true;
  }, [userId, filters?.loc, router.query]);

  useEffect(() => {
    if (calledType !== 'tag' && calledType !== 'category') {
      return;
    }
    if (
      hasTracked.current &&
      !hasPageNoChanged.current &&
      !hasFilterChanged.current
    )
      return;
    let actionType: string =
      pageNumber > filters.page + 1
        ? 'scroll'
        : initialPageTracked.current
        ? 'scroll'
        : 'view';
    if (hasFilterChanged.current) {
      actionType = 'filter';
    }

    const filterLocation = Array.isArray(filters?.loc)
      ? filters?.loc.join(',')
      : filters?.loc || '';

    const sessionUserId = userIdSSR || userId || '-1';

    hasTracked.current = true;
    hasPageNoChanged.current = false;
    hasFilterChanged.current = false;

    TrackEventUBA('locationListingPageView', actionType, {
      ref: router.query.ref || '',
      filterExp: currentFilters?.maxexp
        ? `${currentFilters.minexp}-${currentFilters.maxexp}`
        : '',
      filterLocation,
      filterIndustry: currentFilters?.industry?.join(',') || '',
      pageName: 'LocationListingPage',
      pageNo: pageNumber,
      categoryID: calledType === 'category' ? Number(id) : '',
      TagID: calledType === 'tag' ? Number(id) : '',
      locationID: Number(filterLocation),
      sessionUsername: sessionUserId,
    });
    initialPageTracked.current = true;
  }, [userId, filters?.loc, router.query, pageNumber, currentFilters]);

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout
          seoConfig={
            noIndexNoFollow
              ? {...seoConfig, noindex: true, nofollow: true}
              : seoConfig
          }
          paginationEnabled={true}
        />
        <NavLayout showbottom={true}>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={breadcrumbElements}
          >
            <JobFeed
              jobFeedType={LOCATION_BASED}
              catOrTagId={id}
              catOrTagTitle={title}
              showProfileCard={false}
              showFeatured={isAppIimjobs ? true : false}
              showLearning={true}
              preFilters={filters}
              setPageNumber={setPageNumber}
              onFiltersChange={setCurrentFilters}
              calledType={calledType}
              nearByCitiesJobs={nearByCitiesJobs}
              enablePagination={true}
              showMultiApply={showMultiApply}
            />
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const pageNo = parseInt(context.query?.page) || 0;
    const minExp = parseInt(context.query?.minexp) || 0;
    const maxExp = parseInt(context.query?.maxexp) || 0;
    const industry = context.query?.industry?.split(',') || [];
    const slugPath = context?.resolvedUrl.split('/jobs/')[1].split('?')[0];

    const regex = /^([a-z0-9-&]+)-jobs(?:-in-([a-z0-9-&]+))?$/;

    const match = slugPath?.match(regex);

    if (!match) {
      return {
        notFound: true,
      };
    }

    if (
      HIRIST_CATEGORY_ROUTE_MAPPING[slugPath?.split('-jobs-in-')?.[0] ?? '']
    ) {
      const splittedSlug = slugPath?.split('-jobs-in-');
      const mappedSlug = splittedSlug?.[0]?.replace('/', '')?.replace('&-', '');
      const path = `/${HIRIST_CATEGORY_ROUTE_MAPPING[mappedSlug]}-jobs-in-${splittedSlug?.[1]}`;
      return {
        redirect: {
          permanent: true,
          destination: path,
        },
      };
    }

    const keyword = '-jobs-in-';

    const getLastSplit = (slugPath = '') => {
      try {
        const lastIndex = slugPath.lastIndexOf(keyword);

        if (lastIndex === -1) return [slugPath, ''];

        const before = slugPath.substring(0, lastIndex);
        const after = slugPath.substring(lastIndex + keyword.length);

        return [before, after];
      } catch {
        return slugPath?.split('-jobs-in-');
      }
    };
    const splittedURL = getLastSplit(slugPath);

    const slugUrl = splittedURL?.[0]?.replace('/', '')?.replace('&-', '');

    const locationSlug = splittedURL?.[1]?.split('?')?.[0];

    const capitalisedLocation = capitalizeFirstLetter(locationSlug);
    if (
      !isAppIimjobs &&
      locationSlug &&
      !HIRIST_LOCATION_LABELS.has(normalizeLocation(locationSlug))
    ) {
      return {
        notFound: true,
      };
    }

    const location = getSelectedLocationIds(capitalisedLocation);

    const metaData = getConfigByApp(metaInfo, JOBFEED, true, slugPath);

    let userId = -1;

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });

          userId = parseInt(validateInfo?.userId ?? '0', 10);
        }
      }
    } catch (err) {}

    const {
      header: {
        meta: {tabsList},
      },
      ads: {
        featuredEmployer,
        diversity,
        learnGrow,
        featuredInstitutes,
        learningPartners,
      },
    } = metaData || {};

    const {id, title} = getIdBySlug(
      'categoryLocation',
      slugUrl,
      tabsList,
      false,
      isAppIimjobs ? false : true
    );

    let slugId = id;
    let slugTitle = title;
    let type = 'category';
    let slugCatId = 0;

    if (!slugTitle) {
      const {id, title, catId} = getIdBySlug('tag', slugUrl, tabsList);
      slugId = id;
      slugTitle = title;
      type = 'tag';
      slugCatId = catId ?? 0;
    }

    const ssrHeaders = {
      'user-agent': context.req?.headers?.['user-agent'],
      'x-forwarded-for': context.req?.headers?.['x-forwarded-for']
        ?.toString()
        .split(',')[0],
      referer: `https://www.${process.env.NEXT_PUBLIC_APP_DOMAIN}/`,
    };

    if (!slugId) {
      const tagInfo = await jobFeedService.tagIdBySlug(
        slugUrl?.replace(/-jobs$/, ''),
        ssrHeaders
      );

      slugId = tagInfo?.id;
      slugTitle = tagInfo?.title;
      slugCatId = tagInfo?.catId ?? 0;
    }

    if ((!slugId || !location) && isAppIimjobs) {
      return {
        notFound: true,
      };
    }
    if (
      ((!slugId && !location) || slugUrl == 'ecommerce-jobs') &&
      !isAppIimjobs
    ) {
      const resp = await jobDetailService.getCompanyIdBySlug(
        slugUrl?.split('?')[0],
        ssrHeaders
      );
      slugId = resp?.data?.slug;
      type = 'company';
    }
    if (!slugId) {
      const hirist_extra = TAG_ID_HIRIST.get(slugUrl.concat('-jobs'));
      if (hirist_extra) {
        slugId = hirist_extra.id;
        slugTitle = hirist_extra.tagName;
      }
    }
    if (!slugId && !location) {
      return {
        notFound: true,
      };
    }

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: type == 'company' ? CAT_LOCATION_BASED : LOCATION_BASED},
    });

    if (slugId && parseInt(slugId) > -1) {
      store.dispatch({
        type: 'AppContext/set_navigation_selector',
        payload: type === 'tag' ? slugCatId : parseInt(slugId),
      });

      store.dispatch({
        type: 'AppContext/set_category_actual',
        payload: type === 'tag' ? slugCatId : parseInt(slugId),
      });
    }

    if (slugId) {
      store.dispatch({
        type: 'AppContext/set_tag_selector',
        payload: slugId,
      });
    }

    if (typeof window === 'undefined') {
      store.dispatch({
        type: 'AppContext/set_enable_ads_preload',
        payload: true,
      });
    }

    store.dispatch({type: 'job/jobFeed/set_initial_state'});

    const filters = {
      page: pageNo > 0 ? pageNo - 1 : 0,
      minexp: minExp,
      maxexp: maxExp,
      loc: location,
      industry,
    };

    const isBotUser = isUserAgentForBot(context);
    const titleMapping: Record<string, string> = {
      'ecommerce-jobs': 'E-Commerce Companies Jobs',
      'product-jobs': 'Tech Product Companies Jobs',
      'fintech-edtech-jobs': 'Fintech-EdTech-Gaming Companies Jobs',
    };

    console.log('🚀 ~ file: [slug].tsx:160 ~ store=> ~ isBotUser:', isBotUser);
    let totalJobs = 0;
    let count1Year: number | null = null;
    let nearByCitiesJobs = {
      hasMore: false,
      jobs: [],
    };
    if (isBotUser) {
      /**
       * Fetch the initial state for jobs list and related ads to show
       */
      const fetchCityJobs = await jobfeedService.getNearByCityJobs({
        userId,
        minexp: filters?.minexp,
        maxexp: filters?.maxexp,
        loc: filters?.loc,
        ...(type == 'category'
          ? {catId: slugId || (isAppIimjobs ? '-1' : '')}
          : {query: slugId || (isAppIimjobs ? '-1' : '')}),
        headers: ssrHeaders,
      });
      nearByCitiesJobs = {hasMore: false, jobs: fetchCityJobs?.data};

      store.dispatch({
        type: 'AppContext/set_bot',
        payload: true,
      });
      console.log({slugId, type});

      try {
        const jobFeeds: any = await jobFeedService.getLocationSpecificJobs({
          userId: '-1',
          id: slugId,
          type,
          pageNo: pageNo > 0 ? pageNo - 1 : 0,
          minexp: minExp,
          maxexp: maxExp,
          loc: location,
          size: 100,
          headers: ssrHeaders,
        });
        if (type == 'company') {
          jobFeeds.totalJobs = jobFeeds.jobs.length;
        }
        totalJobs = jobFeeds?.totalJobs;
        count1Year =
          typeof jobFeeds?.count1Year === 'number' ? jobFeeds.count1Year : null;

        store.dispatch({
          type: 'job/jobFeed/set_jobfeed',
          payload: jobFeeds,
        });
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:200 ~ store jobfeed category jobs=> ~ error:',
          error
        );
        return {
          notFound: true,
        };
      }

      try {
        const categoryId = type === 'tag' ? slugCatId?.toString() : slugId;

        if (featuredEmployer?.slot) {
          const ads = await loadAds(featuredEmployer.slot, {
            categoryIds: categoryId,
          });
          if (ads) {
            store.dispatch({
              type: 'Ads/set_featured_employer',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (diversity?.slot) {
          const ads = await loadAds(diversity.slot);
          if (ads) {
            store.dispatch({
              type: 'Ads/set_diversity',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (learnGrow?.slot) {
          const ads = await loadAds(
            learnGrow.slot,
            {
              categoryIds: categoryId,
              page: 0,
              size: 20,
              user_location: 0,
              shuffle: 1,
              orderby: 'modified',
            },
            true
          );
          if (ads) {
            store.dispatch({
              type: 'Ads/set_learn_grow',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (featuredInstitutes?.slot) {
          const ads = await loadAds(featuredInstitutes.slot, {
            categoryIds: categoryId,
          });
          if (ads) {
            store.dispatch({
              type: 'Ads/set_featured_institutes',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (learningPartners?.slot) {
          const ads = await loadAds(learningPartners.slot);

          if (ads) {
            store.dispatch({
              type: 'Ads/set_learning_partner',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:277 ~ store=> ~ category jobs error:',
          error
        );

        return {
          notFound: true,
        };
      }
    } else {
      // Real (non-bot) users: minimal fetch (size: 1) for count1Year only, so
      // the SSR HTML emits the correct robots directive (avoids the orphaned
      // index,follow that a client-only override leaves behind).
      try {
        const jobFeeds: any = await jobFeedService.getLocationSpecificJobs({
          userId: '-1',
          id: slugId || (isAppIimjobs ? '-1' : ''),
          type,
          pageNo: pageNo > 0 ? pageNo - 1 : 0,
          minexp: minExp,
          maxexp: maxExp,
          loc: location,
          size: 1,
          headers: ssrHeaders,
        });
        count1Year =
          typeof jobFeeds?.count1Year === 'number' ? jobFeeds.count1Year : null;
      } catch (error) {
        // Don't block page render if the count lookup fails.
      }
    }

    let seoTitle = '';
    if (slugTitle) {
      seoTitle = `${
        slugTitle?.includes('Jobs') ? slugTitle : `${slugTitle} Jobs`
      } in ${
        isAppIimjobs
          ? capitalisedLocation?.replaceAll('-', ' ')
          : getMatchingCityName(capitalisedLocation)
      }`;
    }
    const baseSlugTitle =
      slugUrl == 'it'
        ? `IT jobs in ${getMatchingCityName(capitalisedLocation)}`
        : slugToTitle(slugUrl);

    let slugtitle = baseSlugTitle;
    if (!/jobs?\s+in/i.test(baseSlugTitle) && !isAppIimjobs && type === 'tag') {
      slugtitle = `${capitalizeWords(baseSlugTitle)} Jobs in ${
        isAppIimjobs
          ? capitalisedLocation?.replaceAll('-', ' ')
          : getMatchingCityName(capitalisedLocation)
      }`;
    }
    const seoConfig = await loadSEO(
      LOCATION,
      getListSEOContent(
        seoTitle || slugtitle,
        pageNo,
        totalJobs + nearByCitiesJobs?.jobs?.length,
        true
      ),
      getPageUrl(`/${slugPath}`, pageNo),
      true,
      pageNo
    );

    if (count1Year === 0 && seoConfig) {
      seoConfig.noindex = true;
      seoConfig.nofollow = true;
    }

    if (slugTitle) {
      store.dispatch({
        type: 'AppContext/set_slug',
        payload: slugTitle,
      });
      store.dispatch({
        type: 'AppContext/set_slug_url',
        payload: slugUrl,
      });
    }

    store.dispatch({
      type: 'AppContext/set_naviagtion_location',
      payload: capitalisedLocation,
    });

    const showMultiApply = await getMultiApplyVariant(context);

    return {
      props: {
        userIdSSR: userId,
        slug: slugUrl || '',
        title:
          type === 'company'
            ? titleMapping[String(slugUrl)] || slugToTitle(slugUrl)
            : seoTitle || slugtitle,
        id: slugId || (isAppIimjobs ? '-1' : ''),
        calledType: type,
        metaData,
        seoConfig,
        filters,
        nearByCitiesJobs,
        showMultiApply,
      },
    };
  }
);

export default JobWithLocationPage;
